import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import portalApi from "@/lib/portalApi";
import { MapPin, ChevronRight } from "lucide-react";

export const STATUS_LABELS = {
  planned: ["Planned", "bg-blue-100 text-blue-700"],
  in_progress: ["In progress", "bg-amber-100 text-amber-700"],
  on_hold: ["On hold", "bg-zinc-200 text-zinc-600"],
  completed: ["Completed", "bg-emerald-100 text-emerald-700"],
  cancelled: ["Cancelled", "bg-red-100 text-red-700"],
};

export function StatusChip({ status }) {
  const [label, cls] = STATUS_LABELS[status] || [status || "—", "bg-zinc-100 text-zinc-600"];
  return (
    <span className={`inline-block px-2.5 py-1 rounded-md text-[11px] font-bold ${cls}`} data-testid="portal-status-chip">
      {label}
    </span>
  );
}

export function ProgressBar({ pct }) {
  if (pct == null) return null;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 rounded-full bg-zinc-100 overflow-hidden">
        <div className="h-full rounded-full bg-[#FF5F15] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-bold text-zinc-700 font-mono">{pct}%</span>
    </div>
  );
}

export default function PortalJobs() {
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    portalApi.get("/portal/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  return (
    <div data-testid="portal-jobs-page">
      <h1 className="text-2xl font-extrabold tracking-tight text-zinc-900">Your jobs</h1>
      <p className="text-sm text-zinc-500 mt-1">Tap a job to see progress, photos and updates.</p>
      <div className="mt-5 space-y-3">
        {jobs === null && [0, 1].map((i) => (
          <div key={i} className="h-28 rounded-2xl bg-white border border-zinc-200 animate-pulse" />
        ))}
        {jobs?.length === 0 && (
          <div className="rounded-2xl bg-white border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500">
            No jobs shared with you yet.
          </div>
        )}
        {jobs?.map((j) => (
          <Link key={j.id} to={`/portal/jobs/${j.id}`}
                className="block rounded-2xl bg-white border border-zinc-200 p-5 hover:border-[#FF5F15]/50 hover:shadow-sm transition-all"
                data-testid={`portal-job-${j.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-mono text-zinc-400">{j.job_number}</div>
                <div className="font-bold text-zinc-900 text-[16px] leading-snug mt-0.5">{j.title}</div>
                {j.site_address && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-1.5">
                    <MapPin className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{j.site_address}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusChip status={j.status} />
                <ChevronRight className="w-4 h-4 text-zinc-300" />
              </div>
            </div>
            {j.progress && j.progress.pct != null && (
              <div className="mt-4">
                <ProgressBar pct={j.progress.pct} />
                <div className="text-[11px] text-zinc-400 mt-1.5">{j.progress.tasks_done} of {j.progress.tasks_total} tasks complete</div>
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
